import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { updateMeSchema, type UpdateMeInput } from '@proref/schemas';
import { useAuth } from '../../contexts/auth-context';
import { updateMe } from '../../services/users.service';
import { notifyError, notifySuccess } from '../../lib/toast';
import { Field } from '../../components/shared/Field';
import { Input } from '../../components/shared/Input';
import { Select } from '../../components/shared/Select';
import { Button } from '../../components/shared/Button';
import { Avatar } from '../../components/shared/Avatar';
import { SettingsCard, ReadOnlyRow } from './SettingsCard';
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, type SupportedLanguage } from '../../lib/i18n';

// Profile tab: one editable field, and the account facts around it.
//
// Only the display name is editable, and the read-only rows say why rather than just greying out.
// Email is the login identity — changing it is a re-verification flow, not a text box. Role,
// federation, sport and licence level are credentials somebody else assigns: a referee who could
// set their own licence level could claim any grade they liked.
export function ProfileSettings() {
  const { user, setProfile } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UpdateMeInput>({
    resolver: zodResolver(updateMeSchema),
    // `values` rather than `defaultValues`: the form mounts before the auth bootstrap has
    // necessarily resolved, and defaultValues would freeze the empty string it saw first.
    values: { full_name: user?.full_name ?? '' },
  });

  const onSubmit = handleSubmit(async (input) => {
    try {
      const updated = await updateMe(input);
      // PATCH /v1/me answers with the full /me shape, so push it into the auth context directly —
      // the sidebar chip and the topbar avatar re-render off it with no refetch.
      setProfile(updated);
      // reset() re-baselines isDirty, so Save goes back to disabled until something changes again.
      reset({ full_name: updated.full_name });
      notifySuccess('Profile updated');
    } catch (err) {
      notifyError(err, 'Could not update your profile');
    }
  });

  return (
    <>
      <SettingsCard
        title="Your name"
        description="How you appear on the leaderboard, in discussion and on your federation's roster."
      >
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <div className="flex items-center gap-3">
            <Avatar name={user?.full_name ?? '?'} />
            <div className="min-w-0 flex-1">
              <Field label="Full name" htmlFor="full_name" error={errors.full_name?.message}>
                <Input id="full_name" {...register('full_name')} autoComplete="name" />
              </Field>
            </div>
          </div>
          <div>
            {/* Disabled until something actually changed: a live Save button on an untouched form
                invites a pointless round-trip and an equally pointless success toast. */}
            <Button type="submit" variant="accent" size="sm" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </SettingsCard>

      <LanguageCard />

      <SettingsCard
        title="Account"
        description="Set when you were invited. Ask your federation admin if any of this is wrong."
      >
        <ReadOnlyRow label="Email" value={user?.email ?? '—'} note="Your sign-in address." />
        <ReadOnlyRow label="Role" value={<RoleLabel role={user?.role} />} />
        <ReadOnlyRow
          label="Federation"
          value={user?.federation_name ?? 'ProRef (global)'}
          note={
            user?.federation_name
              ? 'Only ProRef can move you to another federation.'
              : 'A ProRef admin belongs to no single federation.'
          }
        />
        <ReadOnlyRow label="Sport" value={user?.sport_name ?? '—'} />
        <ReadOnlyRow
          label="Licence level"
          value={user?.licence_level ?? 'Not set'}
          note="Assigned by your federation."
        />
      </SettingsCard>
    </>
  );
}

// M15 slice 1: the language picker. A purely client-side preference — i18next-browser-
// languagedetector persists it to localStorage and re-reads it on every load, so there's no
// server round-trip and no `users.locale` column (yet — that only matters once server-generated
// text, e.g. notifications, needs to know a viewer's language too, which is a later slice).
//
// Title/description/label are themselves translated (t()), so this card doubles as the first
// real proof the pipeline works end-to-end, not just infrastructure nobody has used yet.
function LanguageCard() {
  const { t, i18n } = useTranslation();

  return (
    <SettingsCard title={t('settings.language.title')} description={t('settings.language.description')}>
      <Field label={t('settings.language.label')} htmlFor="language">
        <Select
          id="language"
          value={i18n.resolvedLanguage as SupportedLanguage}
          onChange={(e) => void i18n.changeLanguage(e.target.value as SupportedLanguage)}
        >
          {SUPPORTED_LANGUAGES.map((lng) => (
            <option key={lng} value={lng}>
              {LANGUAGE_NAMES[lng]}
            </option>
          ))}
        </Select>
      </Field>
    </SettingsCard>
  );
}

// The DB's role values are snake_case identifiers; nobody should read "federation_admin" in a UI.
function RoleLabel({ role }: { role: string | null | undefined }) {
  if (role === 'referee') return <>Referee</>;
  if (role === 'federation_admin') return <>Federation admin</>;
  if (role === 'super_admin') return <>ProRef admin</>;
  return <>—</>;
}
